/**
 * AcceptInvitePage — aceitação de convite (afiliado / gerente / coprodutor).
 *
 * Fluxo:
 *  1. GET /api/v2/public/team-invites/:token → { email, expired }
 *  2. Form: nome + senha + confirmar senha
 *  3. POST /api/v2/public/team-invites/:token/accept → ativa conta
 *  4. Redirect pro /login com mensagem de sucesso
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { API_URL } from '../config/api';
import PagmusLogo from '../assets/logo/PagmuslogoNegativa.svg';
import '../components/ForgotPasswordLayer.scss';

const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | ready | expired | not_found | error
  const [email, setEmail] = useState('');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('not_found');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/v2/public/team-invites/${token}`);
        if (res.status === 404) {
          setStatus('not_found');
          return;
        }
        const data = await res.json();
        if (!res.ok) {
          setStatus('error');
          return;
        }
        if (data.expired) {
          setStatus('expired');
          setEmail(data.email || '');
        } else {
          setStatus('ready');
          setEmail(data.email || '');
        }
      } catch {
        setStatus('error');
      }
    })();
  }, [token]);

  const validate = () => {
    if (!password || password.length < 8) {
      setError('Senha precisa ter pelo menos 8 caracteres');
      return false;
    }
    if (password !== confirm) {
      setError('As senhas não coincidem');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/v2/public/team-invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, name: name.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || data.error || 'Erro ao aceitar convite');
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setError('Erro de rede. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const Wrapper = ({ children }) => (
    <section className="auth bg-base d-flex flex-wrap" style={{ minHeight: '100vh' }}>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center" style={{ width: '100%', maxWidth: 480, margin: '0 auto' }}>
        <div className="max-w-464-px mx-auto w-100">
          <div className="text-center mb-32">
            <img src={PagmusLogo} alt="Pagmus" style={{ height: 40 }} />
          </div>
          {children}
        </div>
      </div>
    </section>
  );

  if (status === 'loading') {
    return (
      <Wrapper>
        <div className="text-center py-32">
          <Icon icon="mdi:loading" className="spin" width="32" />
          <p className="mt-16" style={{ color: '#6b7280' }}>Carregando convite…</p>
        </div>
      </Wrapper>
    );
  }

  if (status === 'not_found' || status === 'error') {
    return (
      <Wrapper>
        <div className="text-center py-32">
          <Icon icon="mdi:alert-circle-outline" width="48" style={{ color: '#dc2626' }} />
          <span style={{ display: 'block', fontSize: 18, fontWeight: 600, color: '#111', marginTop: 12 }}>
            Convite inválido
          </span>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Este link de convite não foi encontrado ou já foi usado.
          </p>
          <Link to="/login" className="btn btn-primary mt-16" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 8, textDecoration: 'none' }}>
            Ir para login
          </Link>
        </div>
      </Wrapper>
    );
  }

  if (status === 'expired') {
    return (
      <Wrapper>
        <div className="text-center py-32">
          <Icon icon="mdi:clock-alert-outline" width="48" style={{ color: '#d97706' }} />
          <span style={{ display: 'block', fontSize: 18, fontWeight: 600, color: '#111', marginTop: 12 }}>
            Convite expirado
          </span>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            O link enviado para <strong>{email}</strong> expirou. Peça um novo convite ao produtor que te indicou.
          </p>
          <Link to="/login" className="btn btn-primary mt-16" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 8, textDecoration: 'none' }}>
            Ir para login
          </Link>
        </div>
      </Wrapper>
    );
  }

  if (success) {
    return (
      <Wrapper>
        <div className="text-center py-32">
          <Icon icon="mdi:check-circle-outline" width="48" style={{ color: '#059669' }} />
          <span style={{ display: 'block', fontSize: 18, fontWeight: 600, color: '#111', marginTop: 12 }}>
            Conta ativada!
          </span>
          <p style={{ color: '#6b7280', marginTop: 8 }}>
            Redirecionando para o login…
          </p>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="mb-24">
        <span style={{ display: 'block', fontSize: 22, fontWeight: 700, color: '#111', marginBottom: 8 }}>
          Aceitar convite
        </span>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
          Você foi convidado(a) para a Pagmus como <strong>{email}</strong>. Defina sua senha pra continuar.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-16">
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Email
          </label>
          <input
            type="email"
            className="form-control"
            value={email}
            disabled
            style={{ background: '#f9fafb', cursor: 'not-allowed' }}
          />
        </div>

        <div className="mb-16">
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Seu nome (opcional)
          </label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Como você gosta de ser chamado"
            maxLength={120}
          />
        </div>

        <div className="mb-16">
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Nova senha
          </label>
          <input
            type="password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            minLength={8}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="mb-24">
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Confirmar senha
          </label>
          <input
            type="password"
            className="form-control"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repita a senha"
            minLength={8}
            required
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div className="mb-16" style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: '12px 16px', borderRadius: 8, fontSize: 14,
          }}>
            <Icon icon="mdi:alert-circle-outline" width="16" style={{ marginRight: 6 }} />
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={submitting}
          style={{
            background: '#4f46e5', color: '#fff', padding: '12px 24px',
            borderRadius: 8, border: 0, fontWeight: 600, fontSize: 15,
            cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? (
            <>
              <Icon icon="mdi:loading" className="spin" width="16" style={{ marginRight: 8 }} />
              Ativando…
            </>
          ) : (
            'Ativar conta e aceitar'
          )}
        </button>

        <p className="text-center mt-24" style={{ color: '#6b7280', fontSize: 13 }}>
          Já tem conta? <Link to="/login" style={{ color: '#4f46e5', fontWeight: 600 }}>Entrar</Link>
        </p>
      </form>
    </Wrapper>
  );
};

export default AcceptInvitePage;
